import {AdminPanel} from "@/components/layouts/main/AdminPanel";
import {UserPanel} from "@/components/layouts/main/UserPanel";
import {NotificationInfo} from "@/components/ui/general/NotificationInfo";
import {checkAuth} from "@/redux/slices/commonSlices/candidateSlice";
import {
  getNotification,
  setIsMayoly,
  setIsMobile,
  setLanguage,
  setNavigateHistory,
  setWidth,
} from "@/redux/slices/commonSlices/settingsSlice";
import {setAvatarId, setVoiceId} from "@/redux/slices/mlSlices/chatSlice";

import {Suspense, useEffect, useState} from "react";

import {
  authenticatedRoutes,
  createRoutes,
  unauthenticatedRoutes,
} from "@routers/lazyRoutes.jsx";
import "@styles/app.scss";
import {BgLinear} from "@ui/general/BgLinear";
import {Notification} from "@ui/general/Notification";
import {Spinner} from "@ui/general/Spinner";
import {H3} from "@ui/titles/H3";
import Cookies from "js-cookie";
import momentTimezone from "moment-timezone";
import "moment/locale/ru";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {Navigate, Route, Routes, useLocation} from "react-router-dom";

const App = () => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {feedbackStatus} = useSelector((state) => state.ml.chat);
  const {language, navigateHistory, notification} = useSelector(
    (state) => state.common.settings,
  );
  const {auth, role, isLoading} = useSelector(
    (state) => state.common.candidate,
  );
  const [banner, setBanner] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const {t} = useTranslation();

  const authRoutes = createRoutes(authenticatedRoutes);
  const unauthRoutes = createRoutes(unauthenticatedRoutes);

  const isMedical = window.location.host.includes("ava-medical");

  const isTutor =
    window.location.host.includes("aicoach") ||
    window.location.host.includes("avatutor") ||
    window.location.host.includes("localhost");

  useEffect(() => {
    const handleResize = () => {
      dispatch(setIsMobile(window.innerWidth < 768));
      dispatch(setWidth(window.innerWidth));
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(setIsMayoly(window.location.href.includes("mayoly")));
  }, [dispatch, location]);

  useEffect(() => {
    const languageAttribute = document.querySelector("[data-ava-locale]");

    if (languageAttribute) {
      const languageValue = languageAttribute.getAttribute("data-ava-locale");

      Cookies.set("language", languageValue);
      dispatch(setLanguage(languageValue));
    } else {
      Cookies.set("language", "en");
      dispatch(setLanguage("en"));
    }

    dispatch(checkAuth());
  }, []);

  useEffect(() => {
    if (auth) dispatch(getNotification());
  }, [auth]);

  useEffect(() => {
    moment.locale(language);

    /**
     * ------------------------------------------------
     * AVATAR_ID
     * b4fc2d60-3b82-4694-b243-93e9d2bb0242 - medical public
     * cc634703-067c-48f1-a63e-5beee7286d37 - trainer paid
     * ------------------------------------------------
     * VOICE_ID
     * 4f3b1e99-b580-4f05-9b67-a5f585be0232 - medical public
     * 4f3b1e99-b580-4f05-9b67-a5f585be0232 - trainer paid
     * ------------------------------------------------
     */
    if (isMedical || isTutor) {
      dispatch(setAvatarId("b4fc2d60-3b82-4694-b243-93e9d2bb0242"));
      dispatch(setVoiceId("4f3b1e99-b580-4f05-9b67-a5f585be0232"));
    } else {
      dispatch(setAvatarId("cc634703-067c-48f1-a63e-5beee7286d37"));
      dispatch(setVoiceId("4f3b1e99-b580-4f05-9b67-a5f585be0232"));
    }
  }, [language]);

  useEffect(() => {
    if (!navigateHistory) dispatch(setNavigateHistory(location.pathname));
  });

  useEffect(() => {
    if (notification) {
      setBanner({
        date: momentTimezone
          .tz(notification.date, userTimeZone)
          .format("DD.MM.YYYY"),
        time: momentTimezone
          .tz(notification.date, userTimeZone)
          .format("HH:mm"),
      });
    }

    return () => {
      setBanner(null);
    };
  }, [notification]);

  useEffect(() => {
    const rootElement = document.getElementById("root");
    const isChatRoute = location.pathname.startsWith("/chat/");

    if (rootElement) {
      rootElement.classList.toggle("chat-route", isChatRoute);
    }

    return () => {
      if (rootElement) {
        rootElement.classList.remove("chat-route");
      }
    };
  }, [location.pathname]);

  return (
    <Suspense
      fallback={
        <Spinner
          width={80}
          height={80}
        />
      }
    >
      {isLoading ? (
        <BgLinear />
      ) : (
        <>
          {auth && feedbackStatus.state === "completed" && (
            <NotificationInfo duration={60000} />
          )}

          {notification && banner && auth && (
            <Notification>
              <H3>
                <span>
                  {notification.message ||
                    t("notification.message", {
                      date: banner.date,
                      time: banner.time,
                    })}
                </span>
              </H3>
            </Notification>
          )}

          <Routes>
            {auth ? (
              <>
                <Route
                  path={"/"}
                  element={
                    role === "Representative" ? (
                      navigateHistory && navigateHistory !== "/" ? (
                        <Navigate to={navigateHistory} />
                      ) : (
                        <Navigate to={"/simulations/call"} />
                      )
                    ) : navigateHistory && navigateHistory !== "/" ? (
                      <Navigate to={navigateHistory} />
                    ) : (
                      <Navigate to={"/admin-panel/dashboard"} />
                    )
                  }
                />

                <Route
                  path={"/home/*"}
                  element={
                    role === "Representative" ? (
                      navigateHistory && !navigateHistory.includes("home") ? (
                        <Navigate to={navigateHistory} />
                      ) : (
                        <Navigate to={"/simulations/call"} />
                      )
                    ) : navigateHistory && !navigateHistory.includes("home") ? (
                      <Navigate to={navigateHistory} />
                    ) : (
                      <Navigate to={"/admin-panel/dashboard"} />
                    )
                  }
                />

                {authRoutes &&
                  authRoutes.length > 0 &&
                  authRoutes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path}
                      element={route.element}
                    />
                  ))}

                {(role === "Admin" || role === "Manager") && (
                  <Route
                    path={"/admin-panel/*"}
                    element={<AdminPanel />}
                  />
                )}

                {role === "Representative" && (
                  <Route
                    path={"/user-panel/*"}
                    element={<UserPanel />}
                  />
                )}
              </>
            ) : (
              <>
                <Route
                  path={"/"}
                  element={<Navigate to={"/home/overview"} />}
                />

                {unauthRoutes &&
                  unauthRoutes.length > 0 &&
                  unauthRoutes.map((route, index) => (
                    <Route
                      key={index}
                      path={route.path}
                      element={route.element}
                    />
                  ))}

                <Route
                  path={"/simulations/*"}
                  element={<Navigate to={"/home/login"} />}
                />
                <Route
                  path={"/admin-panel/*"}
                  element={<Navigate to={"/home/login"} />}
                />
                <Route
                  path={"/user-panel/*"}
                  element={<Navigate to={"/home/login"} />}
                />
                <Route
                  path={"/chat/:session/:level/:id"}
                  element={<Navigate to={"/home/login"} />}
                />
              </>
            )}

            <Route
              path={"*"}
              element={<Navigate to={"/home/overview"} />}
            />
          </Routes>
        </>
      )}
    </Suspense>
  );
};

export default App;
